import { z } from "zod";
import { featureFlagsStore } from "../../feature-flags.store";

const actionSchema = z.object({
  actionReference: z.object({
    integrationId: z.string().nonempty(),
    actionId: z.string().nonempty(),
    instanceId: z.number().int().nonnegative(),
  }),
});

const recordListInputConfigSchema = z.object({
  recordList: z.discriminatedUnion("oneofKind", [
    z.object({
      oneofKind: z.literal("actionReference"),
      actionReference: z.object({
        integrationId: z.string().nonempty(),
        actionId: z.string().nonempty(),
        instanceId: z.number().int().nonnegative(),
      }),
    }),
  ]),
  metadata: z.record(z.string(), z.string()).optional(),
});

const outputReferenceSchema = z.object({
  source: z.discriminatedUnion("oneofKind", [
    z.object({
      oneofKind: z.literal("actionReference"),
      actionReference: z.object({
        integrationId: z.string().nonempty(),
        actionId: z.string().nonempty(),
        instanceId: z.number().int().nonnegative(),
      }),
    }),
    z.object({
      oneofKind: z.literal("triggerReference"),
      triggerReference: z.object({
        integrationId: z.string().nonempty(),
        triggerId: z.string().nonempty(),
      }),
    }),
  ]),
  outputId: z.string().nonempty(),
  fieldSlug: z.string().optional(),
});

const loopSchema = z.object({
  oneofKind: z.literal("loop"),
  loop: z.object({
    actions: z.array(actionSchema).optional().default([]),
    source: z.discriminatedUnion("oneofKind", [
      z.object({
        oneofKind: z.literal("recordList"),
        recordList: recordListInputConfigSchema,
      }),
      z.object({
        oneofKind: z.literal("outputReference"),
        outputReference: outputReferenceSchema,
      }),
    ]),
  }),
});

// Recursive: an action group's branches arm nests action groups (branches can
// contain branches). z.lazy defers evaluation to parse time, so configSchema
// (declared below) is safely captured by closure -- see the declaration-order
// note further down.
const actionGroupSchema: z.ZodType<unknown> = z.lazy(() =>
  z.object({ instanceId: z.number(), config: configSchema }),
);

const branchSchema = z.object({
  label: z.string(),
  // Wire-legal branch conditions aren't re-validated here (structure is owned
  // by the automation engine); a branch with no conditions is valid -- it
  // always executes.
  conditions: z.unknown().optional(),
  actionGroups: z.array(actionGroupSchema),
});

const branchesSchema = z.object({
  oneofKind: z.literal("branches"),
  // Both the ceiling and the floor are *combined* counts of conditions
  // branches + elseBranch (spec 2.3: the else-branch is the block's last
  // branch, so it counts toward the max). Neither can be expressed as a plain
  // array constraint (elseBranch lives alongside `branches[]`, not inside it),
  // so both are enforced below via superRefine -- which also reads the limits
  // at parse time, so a flag change applies without rebuilding the schema.
  // See maxConditionsBranches / minTotalBranches in feature-flags.store.ts.
  branches: z
    .object({
      label: z.string(),
      branches: z.array(branchSchema),
      elseBranch: z
        .object({
          label: z.string(),
          actionGroups: z.array(actionGroupSchema),
        })
        .optional(),
    })
    .superRefine((value, ctx) => {
      const { maxConditionsBranches, minTotalBranches } =
        featureFlagsStore.limits;
      const total =
        value.branches.length + (value.elseBranch === undefined ? 0 : 1);
      if (total < minTotalBranches) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["branches"],
          message: `A branches block must contain at least ${minTotalBranches} branch (including the else branch)`,
        });
      }
      if (total > maxConditionsBranches) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["branches"],
          message: `A branches block can contain at most ${maxConditionsBranches} branches (including the else branch)`,
        });
      }
    }),
});

// NOTE on declaration order: actionGroupSchema references configSchema only
// inside its z.lazy callback, so it's fine for configSchema to be declared
// after it (the callback runs at parse time, once the module has finished
// evaluating). branchesSchema, however, is referenced directly inside the
// discriminatedUnion array below, so it must be declared before configSchema.
const configSchema = z.discriminatedUnion("oneofKind", [
  z.object({
    oneofKind: z.literal("actions"),
    actions: z.object({
      actions: z.array(actionSchema),
    }),
  }),
  loopSchema,
  branchesSchema,
]);

export const automationValidSchema = z.object({
  automationId: z.string(),
  solutionId: z.string().nonempty(),
  trigger: z.object({
    triggerReference: z.object({
      integrationId: z.string().nonempty(),
      triggerId: z.string().nonempty(),
    }),
    credentialId: z.string().nonempty(),
    inputs: z.array(
      z.object({
        inputId: z.string().nonempty(),
        value: z.object({}).optional(),
      }),
    ),
  }),
  actionGroups: z
    .array(
      z.object({
        instanceId: z.number().int().nonnegative(),
        config: configSchema,
      }),
    )
    .nonempty(),
});
